import React, { useState, useEffect } from "react";
import { styled } from "@mui/material";
import TimeRangePicker from "@wojtekmaj/react-timerange-picker";
import "@wojtekmaj/react-timerange-picker/dist/TimeRangePicker.css";
import "react-clock/dist/Clock.css";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorMessageComponent from "components/FormikErrorMessage";

const StyledTimeRangePicker = styled("div")(({ theme, disabled }) => ({
  position: "relative",
  "& .react-timerange-picker": {
    width: "100%",
    height: "36px",
    "&--disabled": {
      backgroundColor: "#F2F4FF !important"
    }
  },
  "& .react-timerange-picker__wrapper": {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "2px",
    padding: "0 14px",
    height: "100%",
    maxHeight: "36px",
    width: "100%",
    alignItems: "center",
    backgroundColor: disabled ? "transparent" : theme.palette.background.paper,
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:hover": {
      borderColor: !disabled && theme.palette.text.primary,
    },
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}${
        theme.palette.mode === "light" ? "40" : "80"
      }`,
    },
    justifyContent: "start",
  },
  "& .react-timerange-picker__inputGroup": {
    fontSize: "1rem",
    padding: "0",
    flexGrow: "0",
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
  },
  "& .react-timerange-picker__inputGroup__input": {
    color: theme.palette.text.primary,
    "&:focus": {
      outline: "none",
    },
    "&:invalid": {
      background: "none",
    },
  },
  "& .react-timerange-picker__inputGroup__divider": {
    color: theme.palette.text.secondary,
  },
  "& .react-timerange-picker__range-divider": {
    color: theme.palette.text.secondary,
    margin: "0 8px",
  }
}));

const IconContainer = styled("div")({
  position: "absolute",
  right: "14px",
  top: "48%",
  transform: "translateY(-50%)",
  pointerEvents: "none",
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledPlaceholder = styled("div")({
  fontSize: '15px',
  color: '#999999',
  fontWeight: '400',
  border: "1px solid #D7DADD",
  borderRadius: "2px",
  width: "100%",
  display: "block",
  cursor: 'text',
  padding: "0.5rem 1.5625rem 0.5rem 0.75rem !important",
});

const DEFAULT_INTERNAL_VALUE = [null, null];

const SuiTimeRangePicker = ({
  value,
  onChange,
  name,
  disabled = false,
  onError,
}) => {
  // State to keep track of the current selection
  const [internalValue, setInternalValue] = useState(
    parseTimeRange(value) || DEFAULT_INTERNAL_VALUE
  );
  const [error, setError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  // Convert from string format "hh:mm AM - hh:mm PM" to array format for TimeRangePicker
  function parseTimeRange(timeRangeStr) {
    if (!timeRangeStr || typeof timeRangeStr !== "string") {
      return null;
    }

    try {
      const [start, end] = timeRangeStr.split(" - ");
      if (!start || !end) return null;

      // Parse 12-hour time format to 24-hour format
      const parseTwelveHourTime = (timeStr) => {
        const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (!match) return null;

        let [_, hours, minutes, period] = match;
        hours = parseInt(hours, 10);
        minutes = parseInt(minutes, 10);

        // Convert to 24-hour format
        if (period.toUpperCase() === "PM" && hours < 12) {
          hours += 12;
        } else if (period.toUpperCase() === "AM" && hours === 12) {
          hours = 0;
        }

        return `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}`;
      };

      return [parseTwelveHourTime(start), parseTwelveHourTime(end)];
    } catch (e) {
      console.error("Error parsing time range:", e);
      return null;
    }
  }

  // Format time array to "hh:mm AM - hh:mm PM" string
  function formatTimeRange(timeArray) {
    if (!timeArray || !Array.isArray(timeArray) || timeArray.length !== 2) {
      return "";
    }

    const [start, end] = timeArray;
    if (!start || !end) return "";

    const formatTime = (timeStr) => {
      if (!timeStr) return "";

      try {
        const [hours, minutes] = timeStr.split(":").map(Number);

        // Create a date object to help with formatting
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);

        // Format to "hh:mm AM/PM"
        return date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      } catch (e) {
        console.error("Error formatting time:", e);
        return timeStr;
      }
    };

    const formattedStart = formatTime(start);
    const formattedEnd = formatTime(end);

    return `${formattedStart} - ${formattedEnd}`;
  }

  // Convert time string to minutes for comparison
  function timeToMinutes(timeStr) {
    if (!timeStr) return null;

    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  }

  // Validate that end time is after start time
  function validateTimeRange(start, end) {
    if (!start || !end) return null;

    const startMinutes = timeToMinutes(start);
    const endMinutes = timeToMinutes(end);

    if (startMinutes >= endMinutes) {
      return "End time must be after start time";
    }

    return null;
  }

  const handleChange = (newValue) => {
    // If newValue is null, reset both times
    if (!newValue) {
      setInternalValue([null, null]);
      setError(null);
      onChange && onChange("");
      onError && onError(null);
      return;
    }

    // Handle the case where only one time is selected
    const updatedValue = [...newValue];

    // Make sure we preserve any existing part of the time range
    if (updatedValue[0] === null && internalValue[0]) {
      updatedValue[0] = internalValue[0];
    }
    if (updatedValue[1] === null && internalValue[1]) {
      updatedValue[1] = internalValue[1];
    }

    // Update internal value with the user's selection without modification
    setInternalValue(updatedValue);

    // Check if we have both start and end times
    if (updatedValue[0] && updatedValue[1]) {
      // Validate the time range
      const validationError = validateTimeRange(
        updatedValue[0],
        updatedValue[1]
      );

      // Set the error state
      setError(validationError);
      onError && onError(validationError);

      // Format the time range and call onChange, even with error
      // This allows parent components to handle validation too
      const formattedRange = formatTimeRange(updatedValue);
      onChange && onChange(formattedRange);
    } else {
      // If only one time is selected, clear errors
      setError(null);
      onError && onError(null);
    }
  };

  useEffect(() => {
    const parsedValue = parseTimeRange(value);

    if(!value) {
      setInternalValue(DEFAULT_INTERNAL_VALUE);
      return;
    }

    // if (parsedValue) {
      setInternalValue(parsedValue);
      setIsFocused(true)
      // Validate the parsed value
      const validationError = validateTimeRange(parsedValue[0], parsedValue[1]);
      setError(validationError);
      onError && onError(validationError);
    // }
  }, [value]);

  useEffect(()=>{
    if(!disabled) return;

    setError(null)//clean up errors when field get disabled
  }, [disabled])

  return (
    <>
      <StyledTimeRangePicker disabled={disabled} className={error ? "error" : ""}>
        {isFocused ? (
          <TimeRangePicker
            onChange={handleChange}
            value={internalValue}
            autoFocus={!internalValue}
            clearIcon={null}
            clockIcon={null}
            disableClock={true}
            format="h:mm a"
            hourPlaceholder="hh"
            minutePlaceholder="mm"
            // placeholder={placeholder}
            name={name}
            disabled={disabled}
          />
        ) : (
          <StyledPlaceholder onClick={() => setIsFocused(true)}>Select time</StyledPlaceholder>
        )}
        <IconContainer>
          <AccessTimeIcon
            sx={{ color: error ? "#f44336" : "#1264E2", fontSize: "22px" }}
          />
        </IconContainer>
      </StyledTimeRangePicker>
      {error && <ErrorMessageComponent touched={true} error={error} />}
    </>
  );
};

export default SuiTimeRangePicker;
